import { useState } from "react";
import { ArrowUpRight, Check, Copy, Mail, MessageCircle } from "lucide-react";
import { CONTACT, CONTACT_EMAIL } from "@/lib/content";
import { useWhatsApp } from "@/lib/site";
import { SectionHead, Reveal } from "@/components/Kinetic";

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
};

const EmailCard = () => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (await copyText(CONTACT_EMAIL)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <div className="rounded-[28px] bg-ink-2 p-5 ring-1 ring-white/10 min-[400px]:p-6 sm:p-9">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-white/5 text-emerald-dot ring-1 ring-white/10"><Mail className="h-4 w-4" /></span>
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Email</span>
      </div>
      <div className="fit-box mt-6">
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          data-testid="contact-email-link"
          className="fit-email font-display font-bold leading-tight text-white underline decoration-white/20 decoration-2 underline-offset-[8px] transition-colors duration-300 hover:text-emerald-dot hover:decoration-emerald-dot"
        >
          {CONTACT_EMAIL}
        </a>
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-btn" className="btn-primary">
          SEND AN EMAIL <ArrowUpRight className="btn-arrow h-4 w-4" />
        </a>
        <button type="button" onClick={onCopy} data-testid="contact-copy-email-btn" aria-live="polite" className="btn-ghost-dark">
          {copied ? <Check className="h-4 w-4 text-emerald-dot" /> : <Copy className="h-4 w-4" />}
          {copied ? "COPIED" : "COPY EMAIL"}
        </button>
      </div>
    </div>
  );
};

const WhatsAppCard = ({ number }) => (
  <div className="flex flex-col justify-between gap-8 rounded-[28px] bg-emerald p-6 sm:p-9">
    <div className="flex items-center gap-3">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/20"><MessageCircle className="h-4 w-4" /></span>
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">WhatsApp</span>
    </div>
    <a
      href={`https://wa.me/${number}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-chat-btn"
      className="btn w-full bg-white !py-4 text-emerald-deep hover:bg-ink hover:text-white sm:w-auto sm:self-start"
    >
      Chat on WhatsApp <ArrowUpRight className="btn-arrow h-4 w-4" />
    </a>
  </div>
);

export default function Contact() {
  const whatsapp = useWhatsApp();
  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-ink py-24 text-white lg:py-36">
      <div aria-hidden="true" className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-ink-2" />
      <div className="wrap relative grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHead eyebrow="CONTACT" title={CONTACT.heading} hl={[2]} testId="contact-heading" dark>
            <Reveal delay={0.15}>
              <p data-testid="contact-intro" className="mt-6 max-w-md text-base leading-relaxed text-white/70 sm:text-lg">{CONTACT.intro}</p>
            </Reveal>
          </SectionHead>
        </div>
        <div className="grid gap-4 lg:col-span-7">
          <Reveal y={30}><EmailCard /></Reveal>
          {whatsapp && <Reveal y={30} delay={0.1}><WhatsAppCard number={whatsapp} /></Reveal>}
        </div>
      </div>
    </section>
  );
}
